/**
 * work.tsx — the pipeline work pages downstream of elicitation:
 *   /evidence  capture graph        /interpret  synthesize + review
 *   /author    asset versions        /evaluate   validity findings
 *   /package   doctrine packs        /project    runtime projection + lineage
 *
 * Each page reads the folded workspace and offers exactly the moves that legitimately
 * advance an artifact along the boundary ladder. The downloads make outputs usable.
 */
import { useMemo, useState } from "react";
import { useWorkspace } from "../lib/hooks.ts";
import { useAction } from "../lib/actions.ts";
import { navigate, Link } from "../lib/router.tsx";
import { Icon } from "../lib/format.tsx";
import { useI18n, useT } from "../lib/i18n.tsx";
import type { SemanticAsset } from "../../domain/types.ts";

function PageHead({ no, title, desc, actions }: { no: string; title: string; desc: string; actions?: JSX.Element | undefined }): JSX.Element {
  return (
    <div className="page-head">
      <div>
        <div className="sec-no">{no}</div>
        <h1 className="page-title">{title}</h1>
        <p className="page-desc">{desc}</p>
      </div>
      {actions}
    </div>
  );
}

function download(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="empty-note">{children}</div>;
}

// ── /evidence ─────────────────────────────────────────────────────────────────
export function EvidenceRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const t = useT();
  const items = ws ? Object.values(ws.evidence) : [];
  return (
    <div className="wrap">
      <PageHead
        no="stage 01 · capture"
        title={t("evidence.title")}
        desc={t("evidence.desc")}
        actions={items.length > 0 ? <button className="btn ghost" onClick={() => download("evidence.json", items)}><Icon name="doc" /> {t("common.export")}</button> : undefined}
      />
      {items.length === 0 ? (
        <Empty>{t("evidence.empty")}</Empty>
      ) : (
        <div className="grid two">
          {items.map((e) => (
            <div className="specimen" key={e.evidenceId}>
              <div className="spec-tab">
                <span className="spec-id"><b>{e.sourceType}</b> · {e.evidenceId.split(":").pop()}</span>
                <span className={`pill ${e.interpretationStatus === "interpreted" ? "ok" : "trace"}`}>{e.interpretationStatus}</span>
              </div>
              <div className="spec-body">
                <div className="foot-v" style={{ fontSize: 14.5, color: "var(--paper)", marginBottom: 10 }}>{e.normalizedSummary}</div>
                <div className="evidence-out show">
                  <div className="ev-row"><span className="k">constructTargets:</span> [{e.constructTargets.join(" · ")}]</div>
                  <div className="ev-row"><span className="k">evidenceBoundary:</span> <span className="b">{e.evidenceBoundary}</span></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── /interpret ──────────────────────────────────────────────────────────────
export function InterpretRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const { run, busy } = useAction();
  const t = useT();
  const { locale } = useI18n();
  const items = ws ? Object.values(ws.interpretation) : [];
  return (
    <div className="wrap">
      <PageHead no="stage 02–03 · synthesize + review" title={t("interpret.title")} desc={t("interpret.desc")} />
      {items.length === 0 ? (
        <Empty>{t("interpret.empty")}</Empty>
      ) : (
        <div className="stack">
          {items.map((it) => (
            <div className="specimen" key={it.interpretationId}>
              <div className="spec-tab">
                <span className="spec-id"><b>candidate</b> · confidence {it.confidence}</span>
                <span className={`pill ${it.reviewStatus === "confirmed" ? "ok" : it.reviewStatus === "rejected" ? "danger" : it.reviewStatus === "pending" ? "warn" : "trace"}`}>{it.reviewStatus}</span>
              </div>
              <div className="spec-body">
                <div className="spec-q" style={{ fontSize: 18 }}>{it.claim}</div>
                {it.rationale ? <p className="foot-v" style={{ marginBottom: 10 }}>{it.rationale}</p> : null}
                {it.counterEvidenceRefs.length > 0 ? (
                  <div className="echo">
                    <span className="tag">counter-evidence kept attached</span>
                    {it.counterEvidenceRefs.map((c, i) => (<div key={i}>▸ {c}</div>))}
                  </div>
                ) : null}
                <div className="evidence-out show">
                  <div className="ev-row"><span className="k">evidenceRefs:</span> {it.evidenceRefs.length} item(s)</div>
                  <div className="ev-row"><span className="k">evidenceBoundary:</span> <span className="b">{it.evidenceBoundary}</span></div>
                </div>
                {it.reviewStatus === "pending" ? (
                  <div className="afford">
                    <span className="lbl">{t("interpret.decision")}</span>
                    {(["confirm", "correct", "reject", "defer"] as const).map((k) => (
                      <button
                        key={k}
                        className="aff"
                        data-k={k}
                        disabled={busy}
                        onClick={() => {
                          const note = k === "correct" || k === "reject" ? window.prompt(`Note for "${k}" (optional):`) ?? undefined : undefined;
                          void run("interpretation.review", { interpretationId: it.interpretationId, decision: k, ...(note ? { note } : {}) });
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                ) : it.reviewStatus === "confirmed" ? (
                  <div className="btn-row">
                    <button
                      className="btn primary"
                      disabled={busy}
                      onClick={async () => {
                        const ack = await run("asset.author", { interpretationId: it.interpretationId, locale });
                        if (ack?.goto) navigate(ack.goto.slice(1));
                      }}
                    >
                      {t("interpret.author")}
                    </button>
                    <span className="mini">a confirmed interpretation may become a draft asset</span>
                  </div>
                ) : (
                  <p className="mini" style={{ marginTop: 12 }}>Review recorded: {it.reviewStatus}{it.reviewNote ? ` — "${it.reviewNote}"` : ""}.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── /author ─────────────────────────────────────────────────────────────────
export function AuthorRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const t = useT();
  const items = ws ? Object.values(ws.asset) : [];
  return (
    <div className="wrap">
      <PageHead
        no="stage 04 · author"
        title={t("author.title")}
        desc={t("author.desc")}
        actions={items.length > 0 ? <button className="btn ghost" onClick={() => download("assets.json", items)}><Icon name="doc" /> {t("common.export")}</button> : undefined}
      />
      {items.length === 0 ? (
        <Empty>{t("author.empty")}</Empty>
      ) : (
        <div className="stack">{items.map((a) => <AssetCard key={a.assetId} asset={a} />)}</div>
      )}
    </div>
  );
}

function AssetCard({ asset }: { asset: SemanticAsset }): JSX.Element {
  const { run, busy } = useAction();
  const t = useT();
  const { locale } = useI18n();
  const approved = asset.status === "approved";
  return (
    <div className="specimen">
      <div className="spec-tab">
        <span className="spec-id"><b>{asset.title}</b> · v{asset.version}</span>
        <span className={`pill ${approved ? "ok" : asset.status === "reviewed" ? "trace" : "warn"}`}>
          {approved ? "⬡ approved · frozen" : asset.status}
        </span>
      </div>
      <div className="spec-body">
        <div className="spec-q" style={{ fontSize: 17 }}>{asset.claim}</div>
        <div className="foot-k">behavioral implications</div>
        <ul style={{ margin: "6px 0 12px 18px", color: "var(--paper-dim)", fontSize: 13.5, lineHeight: 1.6 }}>
          {asset.behavioralImplications.map((b, i) => (<li key={i}>{b}</li>))}
        </ul>
        {asset.exceptions.length > 0 ? (
          <>
            <div className="foot-k">exceptions</div>
            <ul style={{ margin: "6px 0 12px 18px", color: "var(--amber)", fontSize: 13.5, lineHeight: 1.6 }}>
              {asset.exceptions.map((b, i) => (<li key={i}>{b}</li>))}
            </ul>
          </>
        ) : null}
        <div className="evidence-out show">
          <div className="ev-row"><span className="k">evidenceRefs:</span> {asset.evidenceRefs.length} · <span className="k">retrievalCues:</span> [{asset.retrievalCues.join(" · ")}]</div>
          <div className="ev-row"><span className="k">projectionEligible:</span> {String(asset.projectionEligible)}</div>
        </div>
        <div className="btn-row">
          {!approved ? (
            <>
              {asset.status === "draft" ? (
                <button className="btn ghost" disabled={busy} onClick={() => void run("asset.revise", { assetId: asset.assetId, markReviewed: true })}>{t("author.markReviewed")}</button>
              ) : null}
              <button className="btn warn" disabled={busy} onClick={() => void run("asset.approve", { assetId: asset.assetId })}><Icon name="lock" /> {t("author.approve")}</button>
            </>
          ) : (
            <span className="mini">Frozen at v{asset.version} · {asset.approvedAt?.slice(0, 16).replace("T", " ")}</span>
          )}
          <button className="btn ghost" disabled={busy} onClick={async () => { const ack = await run("evaluation.run", { assetId: asset.assetId, locale }); if (ack?.goto) navigate(ack.goto.slice(1)); }}>
            <Icon name="scope" /> {t("author.evaluate")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── /evaluate ─────────────────────────────────────────────────────────────────
export function EvaluateRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const t = useT();
  const items = ws ? Object.values(ws.evaluation) : [];
  return (
    <div className="wrap">
      <PageHead no="stage · evaluate" title={t("evaluate.title")} desc={t("evaluate.desc")} />
      {items.length === 0 ? (
        <Empty>{t("evaluate.empty")}</Empty>
      ) : (
        <div className="stack">
          {items.map((ev) => (
            <div className="specimen" key={ev.evaluationRunId}>
              <div className="spec-tab">
                <span className="spec-id"><b>evaluation</b> · {ev.targetRef.split(":").pop()}</span>
                <span className={`pill ${ev.verdict === "pass" ? "ok" : ev.verdict === "fail" ? "danger" : "warn"}`}>{ev.verdict}</span>
              </div>
              <div className="spec-body">
                <table className="reg">
                  <thead><tr><th>dimension</th><th>verdict</th><th className="hide-sm">note</th></tr></thead>
                  <tbody>
                    {ev.findings.map((f) => (
                      <tr key={f.dimension}>
                        <td className="pname">{f.dimension}</td>
                        <td><span className={`pill ${f.verdict === "pass" ? "ok" : f.verdict === "fail" ? "danger" : "warn"}`}>{f.verdict}</span></td>
                        <td className="hide-sm">{f.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="evidence-out show" style={{ marginTop: 14 }}>
                  <div className="ev-row"><span className="k">gates:</span> {ev.gates.join("; ")}</div>
                  <div className="ev-row"><span className="k">nextActions:</span> {ev.nextActions.join("; ")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── /package ─────────────────────────────────────────────────────────────────
export function PackageRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const { run, busy, status } = useAction();
  const t = useT();
  const approved = ws ? Object.values(ws.asset).filter((a) => a.status === "approved") : [];
  const packs = ws ? Object.values(ws.pack) : [];
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [title, setTitle] = useState("");
  const chosen = useMemo(() => approved.filter((a) => picked[a.assetId]).map((a) => a.assetId), [approved, picked]);

  return (
    <div className="wrap">
      <PageHead no="stage · package" title={t("package.title")} desc={t("package.desc")} />
      <div className="specimen">
        <div className="spec-tab"><span className="spec-id"><b>pack.assemble</b></span><span className="spec-fam">package</span></div>
        <div className="spec-body">
          {approved.length === 0 ? (
            <p className="page-desc">No approved assets yet. Approve at least one asset on the <Link to="/author" className="mono" style={{ color: "var(--phosphor)" }}>Author</Link> page.</p>
          ) : (
            <>
              <div className="field">
                <div className="field-label"><span>pack title</span></div>
                <input className="field-input" placeholder="e.g. Refund judgment doctrine" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="field-label"><span>approved assets to admit</span></div>
              {approved.map((a) => (
                <label className="checkbox" key={a.assetId} style={{ marginBottom: 6 }}>
                  <input type="checkbox" checked={!!picked[a.assetId]} onChange={(e) => setPicked((p) => ({ ...p, [a.assetId]: e.target.checked }))} />
                  <span>{a.title} · v{a.version}</span>
                </label>
              ))}
              <div className="btn-row">
                <button className="btn primary" disabled={!title.trim() || chosen.length === 0 || busy} onClick={async () => { await run("pack.assemble", { title: title.trim(), assetIds: chosen }); setTitle(""); setPicked({}); }}>
                  {t("package.assemble")} ({chosen.length})
                </button>
                {status ? <span className="mini">{status}</span> : null}
              </div>
            </>
          )}
        </div>
      </div>

      {packs.length > 0 ? (
        <div className="stack" style={{ marginTop: 24 }}>
          {packs.map((pk) => (
            <div className="specimen" key={pk.doctrinePackId}>
              <div className="spec-tab">
                <span className="spec-id"><b>{pk.title}</b> · v{pk.version}</span>
                <span className={`pill ${pk.status === "approved" ? "ok" : "warn"}`}>{pk.status}</span>
              </div>
              <div className="spec-body">
                <div className="evidence-out show">
                  <div className="ev-row"><span className="k">assetVersionRefs:</span> [{pk.assetVersionRefs.join(" · ")}]</div>
                  <div className="ev-row"><span className="k">conflictResolution:</span> {pk.conflictResolution.join("; ")}</div>
                  {pk.approvalRecord ? <div className="ev-row"><span className="k">approvalRecord:</span> {pk.approvalRecord}</div> : null}
                </div>
                <div className="btn-row">
                  {pk.status !== "approved" ? (
                    <button className="btn warn" disabled={busy} onClick={() => void run("pack.approve", { doctrinePackId: pk.doctrinePackId, approver: "ui-user" })}><Icon name="lock" /> {t("package.approve")}</button>
                  ) : (
                    <button className="btn primary" onClick={() => navigate("/project")}>{t("package.project")}</button>
                  )}
                  <button className="btn ghost" onClick={() => download(`${pk.title.replace(/\s+/g, "-")}.json`, pk)}><Icon name="doc" /> {t("common.export")}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── /project ─────────────────────────────────────────────────────────────────
export function ProjectRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const { run, busy, status } = useAction();
  const t = useT();
  const approvedPacks = ws ? Object.values(ws.pack).filter((p) => p.status === "approved") : [];
  const projections = ws ? Object.values(ws.projection) : [];
  const traces = ws ? Object.values(ws.trace) : [];
  const [packId, setPackId] = useState("");
  const [agentId, setAgentId] = useState("support-agent");
  const [scope, setScope] = useState("refund-handling:read");

  return (
    <div className="wrap">
      <PageHead no="stage 05–06 · project + operate" title={t("project.title")} desc={t("project.desc")} />
      <div className="specimen">
        <div className="spec-tab"><span className="spec-id"><b>projection.compile</b></span><span className="spec-fam">gated · high risk</span></div>
        <div className="spec-body">
          {approvedPacks.length === 0 ? (
            <p className="page-desc">No approved doctrine packs. Assemble and approve a pack on the <Link to="/package" className="mono" style={{ color: "var(--phosphor)" }}>Package</Link> page first.</p>
          ) : (
            <>
              <div className="field">
                <div className="field-label"><span>doctrine pack</span></div>
                <select className="field-input" value={packId || approvedPacks[0]!.doctrinePackId} onChange={(e) => setPackId(e.target.value)}>
                  {approvedPacks.map((p) => (<option key={p.doctrinePackId} value={p.doctrinePackId}>{p.title} · v{p.version}</option>))}
                </select>
              </div>
              <div className="row">
                <div className="field" style={{ flex: 1 }}>
                  <div className="field-label"><span>agent definition id</span></div>
                  <input className="field-input" value={agentId} onChange={(e) => setAgentId(e.target.value)} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <div className="field-label"><span>projection scope</span></div>
                  <input className="field-input" value={scope} onChange={(e) => setScope(e.target.value)} />
                </div>
              </div>
              <div className="btn-row">
                <button className="btn warn" disabled={busy} onClick={() => void run("projection.compile", { doctrinePackId: packId || approvedPacks[0]!.doctrinePackId, agentDefinitionId: agentId, projectionScope: scope })}>
                  <Icon name="broadcast" /> {t("project.compile")}
                </button>
                {status ? <span className="mini">{status}</span> : null}
              </div>
            </>
          )}
        </div>
      </div>

      {projections.length > 0 ? (
        <div className="stack" style={{ marginTop: 24 }}>
          {projections.map((pr) => (
            <div className="specimen" key={pr.runtimeProjectionId}>
              <div className="spec-tab">
                <span className="spec-id"><b>{pr.agentDefinitionId}</b> · {pr.projectionScope}</span>
                <span className={`pill ${pr.status === "active" ? "ok" : pr.status === "rolledback" ? "danger" : "trace"}`}>{pr.status}</span>
              </div>
              <div className="spec-body">
                <div className="foot-k">context blocks (each traces to an asset version &amp; its evidence)</div>
                <div className="lineage-trace" style={{ margin: "8px 0 12px" }}>
                  {pr.contextBlocks.map((b, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <span style={{ color: "var(--phosphor)" }}>{b.assetRef}</span> <span className="arrow">→</span> {b.instruction}
                      <div style={{ color: "var(--faint)" }}>evidence: [{b.evidenceRefs.join(" · ")}]</div>
                    </div>
                  ))}
                </div>
                <div className="btn-row">
                  {pr.status === "compiled" ? <button className="btn warn" disabled={busy} onClick={() => void run("projection.activate", { runtimeProjectionId: pr.runtimeProjectionId })}><Icon name="play" /> {t("project.activate")}</button> : null}
                  {pr.status === "active" ? <button className="btn ghost" disabled={busy} onClick={() => void run("projection.rollback", { runtimeProjectionId: pr.runtimeProjectionId })}><Icon name="undo" /> {t("project.rollback")}</button> : null}
                  <button className="btn ghost" disabled={busy} onClick={() => { const out = window.prompt("Simulate one agent output under this projection:"); if (out) void run("trace.record", { projectionRef: pr.runtimeProjectionId, output: out }); }}>
                    <Icon name="eye" /> {t("project.recordTrace")}
                  </button>
                  <button className="btn ghost" onClick={() => download(`projection-${pr.runtimeProjectionId.split(":").pop()}.json`, pr)}><Icon name="doc" /> {t("common.export")}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {traces.length > 0 ? (
        <>
          <div className="page-head" style={{ marginTop: 40 }}>
            <div><div className="sec-no">stage 06 · operate</div><h2 className="page-title" style={{ fontSize: "clamp(22px,3vw,32px)" }}>Runtime traces</h2></div>
          </div>
          <div className="stack">
            {traces.map((t) => (
              <div className="specimen" key={t.runtimeTraceId}>
                <div className="spec-tab"><span className="spec-id"><b>trace</b> · {t.runtimeTraceId.split(":").pop()}</span><span className="spec-fam">audited</span></div>
                <div className="spec-body">
                  <div className="spec-q" style={{ fontSize: 16 }}>“{t.output}”</div>
                  <div className="evidence-out show">
                    <div className="ev-row"><span className="k">firedContextBlocks:</span> [{t.firedContextBlocks.join(" · ")}]</div>
                    <div className="ev-row"><span className="k">evidenceLineage:</span> [{t.evidenceLineage.join(" · ")}]</div>
                  </div>
                  <p className="mini" style={{ marginTop: 10 }}>This output traces back through projection → asset → evidence. Behavior is logged, not trusted.</p>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
